import {DateTime} from "luxon";
import {useEffect, useRef} from "react";
import {useInView} from "react-intersection-observer";
import {z as zod} from "zod";
import {LoadingSpinner} from "~/components/reports/loadingSpinner";
import {newReferenceElementHeight, newReferenceElementWidth} from "~/components/reports/reportConstants";
import type {AreaGraphWidget} from "~/components/reports/widgetSpecific/areaGraph";
import type {MultiBarGraphWidget} from "~/components/reports/widgetSpecific/barGraph";
import type {ComboChartWidget} from "~/components/reports/widgetSpecific/comboChart";
import type {DonutChartWidget} from "~/components/reports/widgetSpecific/donutChart";
import type {GaugeWidget} from "~/components/reports/widgetSpecific/gauge";
import type {HorizontalBarGraphWidget} from "~/components/reports/widgetSpecific/horizontalBarGraph";
import type {ImageWidget} from "~/components/reports/widgetSpecific/image";
import type {LineGraphWidget} from "~/components/reports/widgetSpecific/lineGraph";
import {widgetTypeDetails} from "~/components/reports/widgetSpecific/maps/widgetTypeDetails";
import {
    transformChartData,
    transformComboChartData,
    transformNumberTileData,
    transformPieChartData,
    transformStackedBarChartData,
    transformTableData,
    transformHadesColumnMetadataToColumnNameToDataTypeMap,
    buildColumnAttributeIds,
} from "~/components/reports/widgetSpecific/maps/widgetTypeRawDataToTransformedData";
import {widgetTypeToViewWidgetComponentInternal} from "~/components/reports/widgetSpecific/maps/widgetTypeToEditableWidgetComponentInternal";
import {widgetTypeToIsWidgetComplete} from "~/components/reports/widgetSpecific/maps/widgetTypeToIsWidgetComplete";
import type {NumberTileWidget} from "~/components/reports/widgetSpecific/numberTile";
import type {PieChartWidget} from "~/components/reports/widgetSpecific/pieChart";
import type {ProgressBarWidget} from "~/components/reports/widgetSpecific/progressBar";
import type {StackedBarGraphWidget} from "~/components/reports/widgetSpecific/stackedBarGraph";
import type {TableWidget} from "~/components/reports/widgetSpecific/table";
import type {TextWidget} from "~/components/reports/widgetSpecific/text";
import type {EditableWidget, Widget} from "~/components/reports/widgetSpecific/widgets";
import {WidgetLoadingState} from "~/components/reports/widgetSpecific/widgets";
import {WidgetType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import type {
    ReportAction,
    ReportState,
    WidgetDataAction,
    WidgetDataState,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/$reportSlug/-state";
import {ReportActionType} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/$reportSlug/-state";
import {
    EditableReportActionType,
    type EditableReportAction,
    type EditableReportState,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-state";
import {
    Granularity,
    QueryResolverErrorCodes,
    WidgetValueType,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-types";
import {CanvasControlType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import {
    checkWhetherFilterIsComplete,
    commitAtomicFilterTempValue,
    getDatesForDateRangeOption,
    getUpdatedCanvasControls,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-utilities";
import type {AtomicFilter} from "~/typeDefinitions";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {BackendFilterOperations, type BackendFilter} from "~/typeDefinitions";
import {capitalizeFirstLetter} from "~/utilities/utilities";
import React from "react";
import {
    EratoColumnTypes,
    EratoDataTypes,
    type EratoRawData,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/a/_adminScaffold/debugger/-types";
import {useHeimdallUser} from "~/contexts/HeimdallUserContext";

export function WidgetComponent({
    widget,
    elementWidth,
    elementAspectRatio,
    gap,
    pageIndex,
    reportState,
    reportDispatch,
    widgetDataState,
    widgetDataDispatch,
    companyId,
    intellsysBaseUrl,
}: {
    widget: Widget;
    elementWidth: number;
    elementAspectRatio: number;
    gap: number;
    pageIndex: number;
    reportState: ReportState;
    reportDispatch: React.Dispatch<ReportAction>;
    widgetDataState: WidgetDataState;
    widgetDataDispatch: React.Dispatch<WidgetDataAction>;
    companyId: Uuid;
    intellsysBaseUrl: string;
}) {
    const {accessToken} = useHeimdallUser();
    const {ref: widgetRef, inView: widgetInView} = useInView({
        rootMargin: "50%",
        threshold: 0,
    });
    useWidgetData(widget, widgetInView, reportState, reportDispatch, widgetDataState, widgetDataDispatch, companyId, accessToken, intellsysBaseUrl);

    const gridColumnStart: number = widget.col + 1;
    const gridRowStart: number = widget.row + 1;
    const gridColumnSpan: number = widget.colSpan;
    const gridRowSpan: number = widget.rowSpan;
    const gridColumnEnd: number = widget.col + gridColumnSpan;
    const gridRowEnd: number = widget.row + gridRowSpan;
    const widgetWidth = gridColumnSpan * elementWidth + (gridColumnSpan - 1) * gap;
    const widgetHeight = (gridRowSpan * elementWidth) / elementAspectRatio + (gridRowSpan - 1) * gap;

    const WidgetComponentInternalForWidgetType =
        // widgetTypeToEditableWidgetComponentInternal[widget.type];
        widgetTypeToViewWidgetComponentInternal[widget.type];

    const scalingFactor = widgetHeight > widgetWidth ? widgetWidth / newReferenceElementWidth : widgetHeight / newReferenceElementHeight;

    return (
        <div
            className="relative group bg-ds-surface-sunken"
            style={{
                gridColumnStart: gridColumnStart,
                gridRowStart: gridRowStart,
                gridColumnEnd: gridColumnEnd,
                gridRowEnd: gridRowEnd,
                width: widgetWidth,
                height: widgetHeight,
            }}
            id={`gj-${widget.id}`}
            ref={widgetRef}
        >
            {(widget.loadingState === WidgetLoadingState.notStarted || widget.loadingState === WidgetLoadingState.loading) && (
                <div className="absolute h-full w-full z-10 grid place-items-center widget-loading-state">
                    <LoadingSpinner />
                </div>
            )}
            <div className="w-full h-full overflow-hidden border border-ds-border border-solid">
                <WidgetComponentInternalForWidgetType
                    widget={widget}
                    widgetHeight={widgetHeight}
                    widgetWidth={widgetWidth}
                    className={widget.loadingState === WidgetLoadingState.notStarted || widget.loadingState === WidgetLoadingState.loading ? "opacity-20" : ""}
                    scalingFactor={scalingFactor}
                    reportState={reportState}
                    reportDispatch={reportDispatch}
                    widgetDataDispatch={widgetDataDispatch}
                    pageIndex={pageIndex}
                />
            </div>
        </div>
    );
}

function useWidgetData(
    widget: Widget,
    widgetInView: boolean,
    reportState: ReportState,
    reportDispatch: React.Dispatch<ReportAction>,
    widgetDataState: WidgetDataState,
    widgetDataDispatch: React.Dispatch<WidgetDataAction>,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
) {
    const currentWidgetData = widgetDataState.finishedRequests.get(widget.id);

    useEffect(() => {
        // TODO: This can probably be further fine-grained to only load when widgetInView = true or something?
        widgetDataDispatch({
            type: "loadDataForWidgetIfRequired",
            widget: widget,
            reportState: reportState,
            // reportDispatch: reportDispatch,
            companyId: companyId,
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            widgetDataDispatch: widgetDataDispatch,
        });
    }, [
        widget,
        widgetInView,
        // TODO: Remove all json.strigify usages from useEffect dependency arrays
        // JSON.stringify(
        //     getUpdatedCanvasControls(widget, reportState.canvasControls),
        // ),
        // TODO: I have done an even worse thing here - I am fetching everything anytime a
        // canvas control changes. Use fine-grained reactivity instead when invalidating widgets.
        reportState.canvasControls,
        accessToken,
        currentWidgetData,
    ]);

    useEffect(() => {
        if (currentWidgetData === undefined) {
            reportDispatch({
                actionType: ReportActionType.editWidget,
                widget: widget,
                changedFields: {
                    loadingState: WidgetLoadingState.loading,
                },
            });
            // widgetDataDispatch({
            //     type: "loadDataForWidgetIfRequired",
            //     widget: widget,
            //     reportState: reportState,
            //     // reportDispatch: reportDispatch,
            //     companyId: companyId,
            //     accessToken: accessToken,
            //     intellsysBaseUrl: intellsysBaseUrl,
            //     widgetDataDispatch: widgetDataDispatch,
            // });
        } else if (currentWidgetData.success === false) {
            reportDispatch({
                actionType: ReportActionType.editWidget,
                widget: widget,
                changedFields: {
                    loadingState: WidgetLoadingState.errorAfterLoading,
                    errorCode: currentWidgetData.error,
                },
            });
        } else {
            reportDispatch({
                actionType: ReportActionType.editWidget,
                widget: widget,
                changedFields: {
                    loadingState: WidgetLoadingState.loadedSuccessfully,
                    ...currentWidgetData.data,
                },
            });
        }
    }, [currentWidgetData]);
}

export function useEditableWidgetData(
    widget: EditableWidget,
    widgetInView: boolean,
    reportState: EditableReportState,
    reportDispatch: React.Dispatch<EditableReportAction>,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
) {
    // document.addEventListener("beforeunload", () => console.log("beforeUnload"));
    // document.addEventListener("unload", () => console.log("unload"));

    // const requestInProgress = useRef<boolean>(false);
    const requestStatus = useRef<"notStarted" | "inProgress" | "completed" | "errored">("notStarted");

    async function fetchData(abortSignal: AbortSignal) {
        if (widgetTypeDetails[widget.type].isAffectedByTimeRange === false) {
            reportDispatch({
                actionType: EditableReportActionType.editWidget,
                widget: widget,
                changedFields: {
                    loadingState: WidgetLoadingState.notRequired,
                },
            });
            return;
        }

        if (widgetTypeToIsWidgetComplete[widget.type](widget) !== true) {
            reportDispatch({
                actionType: EditableReportActionType.editWidget,
                widget: widget,
                changedFields: {
                    loadingState: WidgetLoadingState.incompleteWidget,
                },
            });
            return;
        }

        requestStatus.current = "inProgress";
        reportDispatch({
            actionType: EditableReportActionType.editWidget,
            widget: widget,
            changedFields: {
                loadingState: WidgetLoadingState.loading,
            },
        });

        let changedFieldsResult: Result<Partial<Widget>, QueryResolverErrorCodes>;
        if (widget.type === WidgetType.multiBarGraph) {
            changedFieldsResult = await fetchDataForMultiBarGraph(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.stackedBarGraph) {
            changedFieldsResult = await fetchDataForStackedBarGraph(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.horizontalBarGraph) {
            changedFieldsResult = await fetchDataForHorizontalBarGraph(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.lineGraph) {
            changedFieldsResult = await fetchDataForLineGraph(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.areaGraph) {
            changedFieldsResult = await fetchDataForAreaGraph(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.comboChart) {
            changedFieldsResult = await fetchDataForComboChart(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.table) {
            changedFieldsResult = await fetchDataForTableWithComparison(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.numberTile) {
            changedFieldsResult = await fetchDataForNumberTile(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.pieChart) {
            changedFieldsResult = await fetchDataForPieChart(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.donutChart) {
            changedFieldsResult = await fetchDataForDonutChart(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.text) {
            changedFieldsResult = await fetchDataForText(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.progressBar) {
            changedFieldsResult = await fetchDataForProgressBar(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.gauge) {
            changedFieldsResult = await fetchDataForGauge(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        } else if (widget.type === WidgetType.image) {
            changedFieldsResult = await fetchDataForImage(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        }
        if (changedFieldsResult.success === false) {
            requestStatus.current = "errored";
            reportDispatch({
                actionType: EditableReportActionType.editWidget,
                widget: widget,
                changedFields: {
                    loadingState: WidgetLoadingState.errorAfterLoading,
                    errorCode: changedFieldsResult.error,
                },
            });
            return;
        }
        const changedFields = changedFieldsResult.data;

        requestStatus.current = "completed";
        reportDispatch({
            actionType: EditableReportActionType.editWidget,
            widget: widget,
            changedFields: {
                loadingState: WidgetLoadingState.loadedSuccessfully,
                ...changedFields,
            },
        });
    }

    const requestTimeout = 30000;

    useEffect(() => {
        if (widgetTypeDetails[widget.type].isAffectedByTimeRange === false) {
            reportDispatch({
                actionType: EditableReportActionType.editWidget,
                widget: widget,
                changedFields: {
                    loadingState: WidgetLoadingState.notRequired,
                },
            });
            return;
        }

        requestStatus.current = "notStarted";
        reportDispatch({
            actionType: EditableReportActionType.editWidget,
            widget: widget,
            changedFields: {
                loadingState: WidgetLoadingState.loading,
            },
        });

        if (widgetInView === true) {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

            fetchData(controller.signal);

            return () => {
                controller.abort();
                clearTimeout(timeoutId);
            };
        }
    }, [
        widget.type,
        JSON.stringify(getUpdatedCanvasControls(widget, reportState.canvasControls)),
        widget.type === WidgetType.multiBarGraph ||
        widget.type === WidgetType.lineGraph ||
        widget.type === WidgetType.areaGraph ||
        widget.type === WidgetType.horizontalBarGraph
            ? `${JSON.stringify(widget.chartSeriesDetails)}${widget.selectedDimensions?.length}${widget.startDateIso}${
                  widget.endDateIso
              }${JSON.stringify(widget.granularity)}`
            : widget.type === WidgetType.stackedBarGraph
              ? `${JSON.stringify(widget.chartSeriesDetails)}${widget.selectedDimensions?.length}${widget.startDateIso}${
                    widget.endDateIso
                }${JSON.stringify(widget.granularity)}${widget.selectedSlicer}`
              : widget.type === WidgetType.comboChart
                ? `${JSON.stringify(widget.chartSeriesDetails)}${widget.selectedDimensions?.length}${widget.startDateIso}${
                      widget.endDateIso
                  }${JSON.stringify(widget.granularity)}`
                : widget.type === WidgetType.numberTile
                  ? `${widget.selectedMetrics?.length}${
                        widget.selectedMetrics == null || widget.selectedMetrics?.length === 0 ? "" : widget.selectedMetrics[0]
                    }${widget.startDateIso}${widget.endDateIso}`
                  : widget.type === WidgetType.pieChart || widget.type === WidgetType.donutChart
                    ? `${widget.selectedMetrics?.length || widget.selectedMetrics[0]}${
                          widget.selectedDimensions?.length || widget.selectedDimensions[0]
                      }${widget.startDateIso}${widget.endDateIso}`
                    : widget.type === WidgetType.table
                      ? `${JSON.stringify(widget.selectedMetrics)}${JSON.stringify(widget.selectedDimensions)}${JSON.stringify(widget.comparison)}`
                      : widget.type === WidgetType.progressBar
                        ? `${widget.selectedMetrics?.length}${widget.startDateIso}${widget.endDateIso}`
                        : widget.type === WidgetType.gauge
                          ? `${widget.selectedMetrics?.length}${widget.startDateIso}${widget.endDateIso}`
                          : widget.type,
        accessToken,
    ]);

    useEffect(() => {
        if (widgetTypeDetails[widget.type].isAffectedByTimeRange === false) {
            return;
        }

        if (widgetInView === false) {
            return;
        }

        if (requestStatus.current === "notStarted") {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

            fetchData(controller.signal);

            return () => {
                // TODO: Had to temporarily remove this - fix it properly
                // controller.abort();
                clearTimeout(timeoutId);
            };
        }
    }, [widgetInView, accessToken]);
}

function getDatesForWidgetDataRequest(
    widget: Widget,
    reportState: ReportState,
): {
    // TODO: This should be Iso8601Date
    startDate: Iso8601DateTime;
    // TODO: This should be Iso8601Date
    endDate: Iso8601DateTime;
} {
    let startDate: Iso8601DateTime;
    let endDate: Iso8601DateTime;

    if (widget.dateRangeCanvasControl != null) {
        const canvasControlId = widget.dateRangeCanvasControl;

        const canvasControl = reportState.canvasControls[canvasControlId];

        if (canvasControl?.type !== CanvasControlType.dateRange || canvasControl.value[0] == null || canvasControl.value[1] == null) {
            startDate = widget.startDateIso == null ? "" : DateTime.fromISO(widget.startDateIso).setZone(reportState.timezone).toISO();
            endDate = widget.endDateIso == null ? "" : DateTime.fromISO(widget.endDateIso).setZone(reportState.timezone).toISO();
        } else if (canvasControl.dateRange != null) {
            const {startDate: startDate_, endDate: endDate_} = getDatesForDateRangeOption(canvasControl.dateRange, reportState.timezone);
            startDate = DateTime.fromISO(startDate_).setZone(reportState.timezone).toISO();
            endDate = DateTime.fromISO(endDate_).setZone(reportState.timezone).toISO();
        } else {
            startDate = DateTime.fromISO(canvasControl.value[0]).setZone(reportState.timezone).toISO();
            endDate = DateTime.fromISO(canvasControl.value[1]).setZone(reportState.timezone).toISO();
        }
    } else {
        startDate = widget.startDateIso == null ? "" : DateTime.fromISO(widget.startDateIso).setZone(reportState.timezone).toISO();
        endDate = DateTime.fromISO(widget.endDateIso).setZone(reportState.timezone).toISO();
    }

    return {
        startDate: startDate,
        endDate: endDate,
    };
}

function getGranularityForWidgetDataRequest(
    widget: Widget,
    reportState: ReportState,
): {
    granularity: Granularity;
} {
    let granularity: Granularity;

    if (widget.granularity != null) {
        if (widget.granularity.type === WidgetValueType.intrinsic) {
            granularity = widget.granularity.value;
        } else {
            granularity = reportState.canvasControls[widget.granularity.value]?.value ?? Granularity.day;
        }
    } else {
        granularity = Granularity.day;
    }

    return {
        granularity: granularity,
    };
}

function getRootFilterForWidgetDataRequest(
    widget: Widget,
    reportState: ReportState,
): {
    rootFilter: BackendFilter | null;
} {
    let rootFilter;

    if (widget.backendFilter != null && widget.backendFilter.filters != null) {
        const newRootFilter = structuredClone(widget.backendFilter);

        const completeFilters = [];

        for (const filter of newRootFilter.filters) {
            const isFilterComplete = checkWhetherFilterIsComplete(filter);
            if (isFilterComplete === false) {
                continue;
            }

            completeFilters.push(filter);
        }

        newRootFilter.filters = structuredClone(completeFilters);

        for (const filter of newRootFilter.filters) {
            const canvasControl = reportState.canvasControls[filter.globalFilterId];
            if (canvasControl !== undefined) {
                if (canvasControl?.type === CanvasControlType.multiSelect) {
                    filter.value = canvasControl.value ?? canvasControl.defaultValue;
                }
            }
        }

        rootFilter = newRootFilter ?? null;
    }

    return {
        rootFilter: rootFilter,
    };
}

export async function fetchData(
    widget: Widget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<Widget>, QueryResolverErrorCodes>> {
    if (widgetTypeDetails[widget.type].isAffectedByTimeRange === false) {
        return dataResult({});
    }

    if (widgetTypeToIsWidgetComplete[widget.type](widget) !== true) {
        return errorResult(QueryResolverErrorCodes.nullInputs);
    }

    if (widget.type === WidgetType.multiBarGraph) {
        return fetchDataForMultiBarGraph(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.stackedBarGraph) {
        return fetchDataForStackedBarGraph(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.horizontalBarGraph) {
        return fetchDataForHorizontalBarGraph(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.lineGraph) {
        return fetchDataForLineGraph(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.areaGraph) {
        return fetchDataForAreaGraph(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.comboChart) {
        return fetchDataForComboChart(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.table) {
        return fetchDataForTableWithComparison(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.numberTile) {
        return fetchDataForNumberTile(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.pieChart) {
        return fetchDataForPieChart(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.donutChart) {
        return fetchDataForDonutChart(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.text) {
        return fetchDataForText(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.progressBar) {
        return fetchDataForProgressBar(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.gauge) {
        return fetchDataForGauge(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else if (widget.type === WidgetType.image) {
        return fetchDataForImage(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    } else {
        return errorResult(QueryResolverErrorCodes.nullInputs);
    }
}

export async function fetchDataForMultiBarGraph(
    widget: MultiBarGraphWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<MultiBarGraphWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = getDatesForWidgetDataRequest(widget, reportState);
    const {granularity} = getGranularityForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        granularity,
        widget.chartSeriesDetails.map((series) => ({
            id: series.selectedMetric,
            distributed: false,
        })),
        widget.selectedDimensions.map((dimension) => ({
            id: dimension,
        })),
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformChartData(
        fetchWidgetData,
        startDate,
        endDate,
        granularity,
        widget.chartSeriesDetails.filter((series) => series.yAxisId === "right").map((series) => series.selectedMetric),
    );

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    const columnMetadata = transformHadesColumnMetadataToColumnNameToDataTypeMap(fetchWidgetData.columnMetadata);
    const columnAttributeIds = buildColumnAttributeIds(fetchWidgetData.columnMetadata);

    return dataResult({
        data: {
            chartData: transformedData.chartData,
            chartSeries: transformedData.chartSeries,
            maxYValue: transformedData.maxYValue,
            minYValue: transformedData.minYValue,
            minYValueSecondary: transformedData.minYValueSecondary,
            maxYValueSecondary: transformedData.maxYValueSecondary,
        },
        xAxisKey: transformedData.dimensionName,
        columnMetadata: columnMetadata,
        columnAttributeIds: columnAttributeIds,
        // TODO: Why is this here?
        title: widget.title ?? transformedData.title,
    });
}

export async function fetchDataForStackedBarGraph(
    widget: StackedBarGraphWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<StackedBarGraphWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = getDatesForWidgetDataRequest(widget, reportState);
    const {granularity} = getGranularityForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        granularity,
        widget.selectedMetrics.map((metricId) => ({
            id: metricId,
            distributed: false,
        })),
        [
            ...widget.selectedDimensions.map((dimension) => ({
                id: dimension,
            })),
            {
                id: widget.selectedSlicer,
            },
        ],
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformStackedBarChartData(fetchWidgetData, startDate, endDate, granularity, widget.selectedSlicer);

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    const columnMetadata = transformHadesColumnMetadataToColumnNameToDataTypeMap(fetchWidgetData.columnMetadata);
    const columnAttributeIds = buildColumnAttributeIds(fetchWidgetData.columnMetadata);

    return dataResult({
        data: {
            chartData: transformedData.chartData,
            chartSeries: transformedData.chartSeries,
            maxYValue: transformedData.maxYValue,
            minYValue: transformedData.minYValue,
            minYValueSecondary: null,
            maxYValueSecondary: null,
        },
        xAxisKey: transformedData.dimensionName,
        columnMetadata: columnMetadata,
        columnAttributeIds: columnAttributeIds,
        // TODO: Why is this here?
        title: widget.title ?? transformedData.title,
    });
}

export async function fetchDataForHorizontalBarGraph(
    widget: HorizontalBarGraphWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<HorizontalBarGraphWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = getDatesForWidgetDataRequest(widget, reportState);
    const {granularity} = getGranularityForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        granularity,
        widget.chartSeriesDetails.map((series) => ({
            id: series.selectedMetric,
            distributed: false,
        })),
        widget.selectedDimensions.map((dimension) => ({
            id: dimension,
        })),
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformChartData(
        fetchWidgetData,
        startDate,
        endDate,
        granularity,
        widget.chartSeriesDetails.filter((series) => series.yAxisId === "right").map((series) => series.selectedMetric),
    );

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    const columnMetadata = transformHadesColumnMetadataToColumnNameToDataTypeMap(fetchWidgetData.columnMetadata);
    const columnAttributeIds = buildColumnAttributeIds(fetchWidgetData.columnMetadata);

    return dataResult({
        data: {
            chartData: transformedData.chartData,
            chartSeries: transformedData.chartSeries,
            maxYValue: transformedData.maxYValue,
            minYValue: transformedData.minYValue,
            minYValueSecondary: transformedData.minYValueSecondary,
            maxYValueSecondary: transformedData.maxYValueSecondary,
        },
        xAxisKey: transformedData.dimensionName,
        columnMetadata: columnMetadata,
        columnAttributeIds: columnAttributeIds,
        title: widget.title ?? transformedData.title,
    });
}

export async function fetchDataForLineGraph(
    widget: LineGraphWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<LineGraphWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = getDatesForWidgetDataRequest(widget, reportState);
    const {granularity} = getGranularityForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        granularity,
        widget.chartSeriesDetails.map((series) => ({
            id: series.selectedMetric,
            distributed: false,
        })),
        widget.selectedDimensions.map((dimension) => ({
            id: dimension,
        })),
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformChartData(
        fetchWidgetData,
        startDate,
        endDate,
        granularity,
        widget.chartSeriesDetails.filter((series) => series.yAxisId === "right").map((series) => series.selectedMetric),
    );

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    const columnMetadata = transformHadesColumnMetadataToColumnNameToDataTypeMap(fetchWidgetData.columnMetadata);
    const columnAttributeIds = buildColumnAttributeIds(fetchWidgetData.columnMetadata);

    return dataResult({
        data: {
            chartData: transformedData.chartData,
            chartSeries: transformedData.chartSeries,
            maxYValue: transformedData.maxYValue,
            minYValue: transformedData.minYValue,
            minYValueSecondary: transformedData.minYValueSecondary,
            maxYValueSecondary: transformedData.maxYValueSecondary,
        },
        xAxisKey: transformedData.dimensionName,
        columnMetadata: columnMetadata,
        columnAttributeIds: columnAttributeIds,
        title: widget.title ?? transformedData.title,
    });
}

export async function fetchDataForNumberTile(
    widget: NumberTileWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<NumberTileWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = getDatesForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        Granularity.day,
        widget.selectedMetrics.map((metricId) => ({
            id: metricId,
            distributed: false,
        })),
        [],
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformNumberTileData(fetchWidgetData);

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    const columnMetadata = transformHadesColumnMetadataToColumnNameToDataTypeMap(fetchWidgetData.columnMetadata);
    const columnAttributeIds = buildColumnAttributeIds(fetchWidgetData.columnMetadata);

    return dataResult({
        aggregatedValue: transformedData.value,
        columnMetadata: columnMetadata,
        columnAttributeIds: columnAttributeIds,
        title: widget.title ?? transformedData.title,
    });
}

export async function fetchDataForPieChart(
    widget: PieChartWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<PieChartWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = getDatesForWidgetDataRequest(widget, reportState);
    const {granularity} = getGranularityForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        granularity,
        widget.selectedMetrics.map((metricId) => ({
            id: metricId,
            distributed: false,
        })),
        widget.selectedDimensions.map((dimension) => ({
            id: dimension,
        })),
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformPieChartData(fetchWidgetData);

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    const columnMetadata = transformHadesColumnMetadataToColumnNameToDataTypeMap(fetchWidgetData.columnMetadata);

    return dataResult({
        data: transformedData.pieChartData,
        title: widget.title ?? transformedData.title,
    });
}

export async function fetchDataForDonutChart(
    widget: DonutChartWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<DonutChartWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = getDatesForWidgetDataRequest(widget, reportState);
    const {granularity} = getGranularityForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        granularity,
        widget.selectedMetrics.map((metricId) => ({
            id: metricId,
            distributed: false,
        })),
        widget.selectedDimensions.map((dimension) => ({
            id: dimension,
        })),
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformPieChartData(fetchWidgetData);

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    const columnMetadata = transformHadesColumnMetadataToColumnNameToDataTypeMap(fetchWidgetData.columnMetadata);
    const columnAttributeIds = buildColumnAttributeIds(fetchWidgetData.columnMetadata);

    return dataResult({
        data: transformedData.pieChartData,
        title: widget.title ?? transformedData.title,
        columnMetadata: columnMetadata,
        columnAttributeIds: columnAttributeIds,
    });
}

export async function fetchDataForText(
    widget: TextWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<TextWidget>, QueryResolverErrorCodes>> {
    return errorResult(QueryResolverErrorCodes.invalidOperation);
}

export async function fetchDataForTable(
    widget: TableWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
    dateRangeOverride?: {startDate: Iso8601DateTime; endDate: Iso8601DateTime},
): Promise<Result<Partial<TableWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = dateRangeOverride ?? getDatesForWidgetDataRequest(widget, reportState);
    const {granularity} = getGranularityForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        granularity,
        widget.selectedMetrics.map((metricId) => ({
            id: metricId,
            distributed: false,
        })),
        widget.selectedDimensions.map((dimension) => ({
            id: dimension,
        })),
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformTableData(fetchWidgetData);

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    const columnMetadata = transformHadesColumnMetadataToColumnNameToDataTypeMap(fetchWidgetData.columnMetadata);
    const columnAttributeIds = buildColumnAttributeIds(fetchWidgetData.columnMetadata);

    return dataResult({
        data: transformedData.data,
        columnMetadata: columnMetadata,
        columnAttributeIds: columnAttributeIds,
        title: widget.title,
    });
}

function getComparisonDataFields(
    comparisonResult: Result<Partial<TableWidget>, QueryResolverErrorCodes> | null,
): Pick<TableWidget, "comparisonData" | "comparisonColumnMetadata" | "comparisonColumnAttributeIds" | "comparisonLoadingState" | "comparisonErrorCode"> {
    if (comparisonResult === null) {
        return {
            comparisonData: null,
            comparisonColumnMetadata: null,
            comparisonColumnAttributeIds: null,
            comparisonLoadingState: WidgetLoadingState.notStarted,
            comparisonErrorCode: null,
        };
    }

    if (comparisonResult.success === false) {
        return {
            comparisonData: null,
            comparisonColumnMetadata: null,
            comparisonColumnAttributeIds: null,
            comparisonLoadingState: WidgetLoadingState.errorAfterLoading,
            comparisonErrorCode: comparisonResult.error,
        };
    }

    return {
        comparisonData: comparisonResult.data.data ?? null,
        comparisonColumnMetadata: comparisonResult.data.columnMetadata ?? null,
        comparisonColumnAttributeIds: comparisonResult.data.columnAttributeIds ?? null,
        comparisonLoadingState: WidgetLoadingState.loadedSuccessfully,
        comparisonErrorCode: null,
    };
}

export async function fetchDataForTableWithComparison(
    widget: TableWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<TableWidget>, QueryResolverErrorCodes>> {
    if (widget.comparison.enabled !== true) {
        return fetchDataForTable(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
    }

    if (widget.comparison.startDateIso === null || widget.comparison.endDateIso === null) {
        const primaryResult = await fetchDataForTable(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal);
        if (primaryResult.success === false) {
            return primaryResult;
        }

        return dataResult({
            ...primaryResult.data,
            ...getComparisonDataFields(null),
        });
    }

    const [primaryResult, comparisonResult] = await Promise.all([
        fetchDataForTable(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal),
        fetchDataForTable(widget, reportState, companyId, accessToken, intellsysBaseUrl, abortSignal, {
            startDate: widget.comparison.startDateIso,
            endDate: widget.comparison.endDateIso,
        }),
    ]);

    if (primaryResult.success === false) {
        return primaryResult;
    }

    return dataResult({
        ...primaryResult.data,
        ...getComparisonDataFields(comparisonResult),
    });
}

export async function fetchDataForGauge(
    widget: GaugeWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<GaugeWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = getDatesForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        Granularity.day,
        widget.selectedMetrics.map((metricId) => ({
            id: metricId,
            distributed: false,
        })),
        [],
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformNumberTileData(fetchWidgetData);

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    return dataResult({
        progressValue: transformedData.value,
        title: widget.title ?? transformedData.title,
    });
}

export async function fetchDataForProgressBar(
    widget: ProgressBarWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<ProgressBarWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = getDatesForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        Granularity.day,
        widget.selectedMetrics.map((metricId) => ({
            id: metricId,
            distributed: false,
        })),
        [],
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformNumberTileData(fetchWidgetData);

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    return dataResult({
        progressValue: transformedData.value,
        title: widget.title ?? transformedData.title,
    });
}

export async function fetchDataForAreaGraph(
    widget: AreaGraphWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<AreaGraphWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = getDatesForWidgetDataRequest(widget, reportState);
    const {granularity} = getGranularityForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        granularity,
        widget.chartSeriesDetails.map((series) => ({
            id: series.selectedMetric,
            distributed: false,
        })),
        widget.selectedDimensions.map((dimension) => ({
            id: dimension,
        })),
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformChartData(
        fetchWidgetData,
        startDate,
        endDate,
        granularity,
        widget.chartSeriesDetails.filter((series) => series.yAxisId === "right").map((series) => series.selectedMetric),
    );

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    const columnMetadata = transformHadesColumnMetadataToColumnNameToDataTypeMap(fetchWidgetData.columnMetadata);
    const columnAttributeIds = buildColumnAttributeIds(fetchWidgetData.columnMetadata);

    return dataResult({
        data: {
            chartData: transformedData.chartData,
            chartSeries: transformedData.chartSeries,
            maxYValue: transformedData.maxYValue,
            minYValue: transformedData.minYValue,
            minYValueSecondary: transformedData.minYValueSecondary,
            maxYValueSecondary: transformedData.maxYValueSecondary,
        },
        xAxisKey: transformedData.dimensionName,
        columnMetadata: columnMetadata,
        columnAttributeIds: columnAttributeIds,
        title: widget.title ?? transformedData.title,
    });
}

export async function fetchDataForComboChart(
    widget: ComboChartWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<ComboChartWidget>, QueryResolverErrorCodes>> {
    const {startDate, endDate} = getDatesForWidgetDataRequest(widget, reportState);
    const {granularity} = getGranularityForWidgetDataRequest(widget, reportState);
    const {rootFilter} = getRootFilterForWidgetDataRequest(widget, reportState);

    const fetchWidgetDataResult = await fetchWidgetDataFromErato(
        widget.datasetId,
        startDate,
        endDate,
        granularity,
        widget.chartSeriesDetails
            .flatMap((series) => series.selectedMetrics)
            .map((metricId) => ({
                id: metricId,
                distributed: false,
            })),
        widget.selectedDimensions.map((dimension) => ({
            id: dimension,
        })),
        rootFilter === null ? null : prepareRootFilterForErato(rootFilter),
        companyId,
        abortSignal,
        accessToken,
        intellsysBaseUrl,
        reportState.timezone,
    );
    if (fetchWidgetDataResult.success === false) {
        return fetchWidgetDataResult;
    }
    const fetchWidgetData = fetchWidgetDataResult.data;

    const transformedData = transformComboChartData(
        fetchWidgetData,
        startDate,
        endDate,
        granularity,
        widget.chartSeriesDetails
            .filter((series) => series.yAxisId === "right")
            .map((series) => series.selectedMetrics)
            .flat(),
        widget.chartSeriesDetails,
    );

    if (transformedData === null) {
        return errorResult(QueryResolverErrorCodes.noDataFound);
    }

    const columnMetadata = transformHadesColumnMetadataToColumnNameToDataTypeMap(fetchWidgetData.columnMetadata);
    const columnAttributeIds = buildColumnAttributeIds(fetchWidgetData.columnMetadata);

    return dataResult({
        data: {
            chartData: transformedData.chartData,
            chartSeries: transformedData.chartSeries,
            maxYValue: transformedData.maxYValue,
            minYValue: transformedData.minYValue,
            minYValueSecondary: transformedData.minYValueSecondary,
            maxYValueSecondary: transformedData.maxYValueSecondary,
        },
        xAxisKey: transformedData.dimensionName,
        columnMetadata: columnMetadata,
        columnAttributeIds: columnAttributeIds,
        title: widget.title ?? transformedData.title,
    });
}

export async function fetchDataForImage(
    widget: ImageWidget,
    reportState: ReportState,
    companyId: Uuid,
    accessToken: string,
    intellsysBaseUrl: string,
    abortSignal: AbortSignal,
): Promise<Result<Partial<ImageWidget>, QueryResolverErrorCodes>> {
    return errorResult(QueryResolverErrorCodes.invalidOperation);
}

export const eratoDataFetchResponseValidator = zod.object({
    columnMetadata: zod.array(
        zod.object({
            id: uuidZodValidator,
            columnType: zod.enum(EratoColumnTypes),
            dataType: zod.enum(EratoDataTypes),
            columnIndex: zod.number(),
            displayName: zod.string(),
        }),
    ),
    dataPoints: zod.array(
        zod.array(
            zod.object({
                value: zod.string().or(zod.number()).or(zod.null()),
            }),
        ),
    ),
});

// TODO: Revisit the optionality/nullability of these parameters + handle null/optional parameters properly
export async function fetchWidgetDataFromErato(
    datasetId: Uuid | null,
    startDate: Iso8601DateTime,
    endDate: Iso8601DateTime,
    granularity: Granularity | null,
    metrics: Array<{
        id: Uuid;
        distributed: boolean;
    }>,
    dimensions: Array<{
        id: Uuid;
    }>,
    filter: BackendFilter | null,
    companyId: Uuid,
    abortSignal: AbortSignal,
    accessToken: string,
    intellsysBaseUrl: string,
    timezone: string,
): Promise<Result<EratoRawData, QueryResolverErrorCodes>> {
    // TODO: Ensure I am not squashing the error code from Erato somehow
    const dataFetchResponseResult = await robustJsonResultClientFetch<EratoRawData>(
        `${intellsysBaseUrl}/sub-system/erato/analytics/dataset/get-data`,
        {
            method: "POST",
            body: JSON.stringify({
                workspaceId: companyId,
                datasetId: datasetId,
                timeRange: {
                    startDate: startDate,
                    endDate: endDate,
                    granularity: capitalizeFirstLetter(granularity ?? Granularity.day),
                    timezone: timezone,
                },
                metrics: metrics,
                dimensions: dimensions,
                filter: filter,
            }),
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
                // "X-Impersonated-User-Id": "a6f43626-ed9c-44a0-bc4e-bc411efbda63",
                // "X-Impersonated-User-Id": "e2b764ac-bddc-409a-ae4b-650f8a3b1a94",
                // ...(intellsysBaseUrl.startsWith("https://intellsys.ai") === true ? {} : {"X-Delay": "5000"}),
            },
            signal: abortSignal,
        },
        eratoDataFetchResponseValidator,
    );
    if (dataFetchResponseResult.success === false) {
        return errorResult(QueryResolverErrorCodes.hadesError);
    }
    const dataFetchResponse = dataFetchResponseResult.data;

    // #region Report-specific ordering of date and category values
    const sortedData = sortDataPointsByDayOfWeek(dataFetchResponse);
    const sortedDataByAgeRange = sortDataPointsByAgeRange(sortedData);
    const sortedDataByDate = sortDataPointsByDate(sortedDataByAgeRange);
    const sortedDataByHour = sortDataPointsByHourOfDay(sortedDataByDate);
    // #endregion

    return dataResult(sortedDataByHour);
}

export function prepareRootFilterForErato(filter: BackendFilter | null) {
    const newFilter = structuredClone(filter);

    // Remove the top-level id key
    if (newFilter == null) {
        return null;
    }
    delete newFilter.id;

    // Process filters
    newFilter.filters = newFilter.filters.map((currentFilter) => {
        const tempFilter = structuredClone(commitAtomicFilterTempValue(currentFilter as AtomicFilter)) as typeof currentFilter;

        if (tempFilter.operation === BackendFilterOperations.in || tempFilter.operation === BackendFilterOperations.between) {
            if (tempFilter.value?.length === 0) {
                tempFilter["values"] = null;
            } else {
                tempFilter["values"] = tempFilter.value;
            }

            delete tempFilter["tempValue"];
            delete tempFilter.value;
        } else {
            delete tempFilter["tempValue"];
        }

        // Remove the id key from each filter
        delete tempFilter.id;

        // Check if the operation key exists
        if (tempFilter.operation != null) {
            // If operation exists, remove filterType
            delete tempFilter.filterType;
        } else {
            // If operation does not exist, rename filterType to operation
            tempFilter.operation = tempFilter.filterType;
            if (tempFilter.filterType === BackendFilterOperations.in) {
                tempFilter["values"] = tempFilter.value;
                delete tempFilter.value;
            }
            delete tempFilter.filterType;
        }

        return tempFilter;
    });

    // Check if the top-level filterType should be renamed to operation
    if (newFilter.operation == null && newFilter.filterType != null) {
        newFilter.operation = newFilter.filterType;
        delete newFilter.filterType;
    }

    const validFilters = [];

    for (const currentFilter of newFilter.filters) {
        if (currentFilter.attribute != null && currentFilter.operation != null && (currentFilter.value != null || currentFilter.values != null)) {
            validFilters.push(currentFilter);
        }
    }

    if (validFilters.length === 0) {
        return null;
    }

    newFilter.filters = validFilters;

    return newFilter;
}

function sortDataPointsByDayOfWeek(response: {
    columnMetadata: Array<{
        id: Uuid;
        columnType: EratoColumnTypes;
        dataType: EratoDataTypes;
        columnIndex: number;
        displayName: string;
    }>;
    dataPoints: Array<Array<{value: string | number | null}>>;
}): typeof response {
    const dayOfWeekColumn = response.columnMetadata.find(
        (col) => col.columnType === "dimension" && col.displayName.toLowerCase() === "Day of Week".toLowerCase(),
    );

    if (dayOfWeekColumn === undefined) {
        return response;
    }

    const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    const sortedDataPoints = [...response.dataPoints].sort((a, b) => {
        const dayA = a[dayOfWeekColumn.columnIndex].value as string;
        const dayB = b[dayOfWeekColumn.columnIndex].value as string;
        return dayOrder.indexOf(dayA) - dayOrder.indexOf(dayB);
    });

    return {
        ...response,
        dataPoints: sortedDataPoints,
    };
}

function sortDataPointsByAgeRange(response: {
    columnMetadata: Array<{
        id: Uuid;
        columnType: EratoColumnTypes;
        dataType: EratoDataTypes;
        columnIndex: number;
        displayName: string;
    }>;
    dataPoints: Array<Array<{value: string | number | null}>>;
}): typeof response {
    const ageRangeColumn = response.columnMetadata.find((col) => col.columnType === "dimension" && col.displayName.toLowerCase() === "Age Range".toLowerCase());

    if (ageRangeColumn === undefined) {
        return response;
    }

    const ageRangeOrder = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+", "Undetermined", "Unknown"];

    const sortedDataPoints = [...response.dataPoints].sort((a, b) => {
        const dayA = a[ageRangeColumn.columnIndex].value as string;
        const dayB = b[ageRangeColumn.columnIndex].value as string;
        return ageRangeOrder.indexOf(dayA) - ageRangeOrder.indexOf(dayB);
    });

    return {
        ...response,
        dataPoints: sortedDataPoints,
    };
}

function sortDataPointsByDate(response: {
    columnMetadata: Array<{
        id: Uuid;
        columnType: EratoColumnTypes;
        dataType: EratoDataTypes;
        columnIndex: number;
        displayName: string;
    }>;
    dataPoints: Array<Array<{value: string | number | null}>>;
}): typeof response {
    const dateColumn = response.columnMetadata.find((col) => col.columnType === "dimension" && col.dataType === EratoDataTypes.date);

    if (dateColumn === undefined) {
        return response;
    }

    const sortedDataPoints = [...response.dataPoints].sort((a, b) => {
        const dateA = a[dateColumn.columnIndex]!.value as string;
        const dateB = b[dateColumn.columnIndex]!.value as string;
        return new Date(dateA).getTime() - new Date(dateB).getTime();
    });

    return {
        ...response,
        dataPoints: sortedDataPoints,
    };
}

function sortDataPointsByHourOfDay(response: {
    columnMetadata: Array<{
        id: Uuid;
        columnType: EratoColumnTypes;
        dataType: EratoDataTypes;
        columnIndex: number;
        displayName: string;
    }>;
    dataPoints: Array<Array<{value: string | number | null}>>;
}): typeof response {
    const hourOfDayColumn = response.columnMetadata.find(
        (col) => col.columnType === "dimension" && col.displayName.toLowerCase() === "Hour of Day".toLowerCase(),
    );

    if (hourOfDayColumn === undefined) {
        return response;
    }

    const sortedDataPoints = [...response.dataPoints].sort(
        (a, b) => (a[hourOfDayColumn.columnIndex]!.value as number) - (b[hourOfDayColumn.columnIndex]!.value as number),
    );

    return {
        ...response,
        dataPoints: sortedDataPoints,
    };
}
